<script setup lang="ts">
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { GpBrandLoading, GpBrandMessage, GpBtn, useFaIcons } from '@gportal/uikit';
import AddBillingProfileModal from '@panel/components/user/billing-profiles/AddBillingProfileModal.vue';
import BillingProfileSection from '@panel/components/user/billing-profiles/BillingProfileSection.vue';
import useUserViewTitle from '@panel/composables/useUserViewTitle';
import SettingsLayout from '@panel/layouts/Layout.User.Settings.vue';
import { useScroll } from '@vueuse/core';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import type { BillingProfile } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

useUserViewTitle('Billing profiles');

useFaIcons({ faPlus });

const showAddModal = ref(false);
const toggleAddModal = () => {
  showAddModal.value = !showAddModal.value;
};

const userStore = useUserStore();
const isLoadingBillingProfiles = ref(false);
const billingProfiles = computed<Array<BillingProfile>>(() => userStore.billingProfiles);
const { addNotification } = useNotifications();

const reloadBillingProfiles = async () => {
  try {
    isLoadingBillingProfiles.value = true;
    await userStore.reloadBillingProfiles();
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isLoadingBillingProfiles.value = false;
  }
};

const { isScrolling, y } = useScroll(window);

// when going back from the bill detail page, we have to open the billing profile section
const route = useRoute();
const billingProfileSections = ref<Array<InstanceType<typeof BillingProfileSection>>>([]);
watch(isLoadingBillingProfiles, async (value) => {
  // wait for the billing profiles to be loaded
  await nextTick();
  await nextTick();

  if (!value && route.query.billingProfile != null) {
    const billingProfileId = route.query.billingProfile as string;
    const targetSection = billingProfileSections.value.find(
      (section) => section.$el.id === `profile__${billingProfileId}`,
    );

    // open the billing profile section if it exists
    if (targetSection != null) {
      targetSection.openCollapse();
    }
  }
});

// auto scroll to new billing profile section
const newBillingProfileSectionRef = ref<InstanceType<typeof BillingProfileSection>>();
const handleCreated = async (billingProfileId: string) => {
  showAddModal.value = false;
  await reloadBillingProfiles();
  await nextTick();

  const targetSection = billingProfileSections.value.find(
    (section) => section.$el.id === `profile__${billingProfileId}`,
  );

  if (
    targetSection != null &&
    targetSection.$el != null &&
    targetSection.$el.getBoundingClientRect().top - 80 < 0
  ) {
    y.value = targetSection.$el.getBoundingClientRect().top + window.scrollY - 80;
    newBillingProfileSectionRef.value = targetSection;
  } else {
    targetSection.highlight();
  }
};

watch(
  () => isScrolling.value,
  (newValue, oldValue) => {
    if (!newValue && oldValue && newBillingProfileSectionRef.value != null) {
      newBillingProfileSectionRef.value.highlight();
      newBillingProfileSectionRef.value = null;
    }
  },
);

onMounted(() => {
  reloadBillingProfiles();
});
</script>

<template>
  <SettingsLayout>
    <template #buttons>
      <GpBtn size="sm" :icon="['fas', 'fa-plus']" label="Create" @click="toggleAddModal" />
    </template>
    <template #content>
      <template v-if="isLoadingBillingProfiles">
        <GpBrandLoading class="tw-mx-auto tw-mt-4" message="Loading billing profiles ..." />
      </template>

      <template v-else-if="billingProfiles.length === 0">
        <GpBrandMessage class="tw-mx-auto tw-mt-4" message="No billing profiles." />
      </template>

      <template v-else>
        <transition-group name="fade">
          <BillingProfileSection
            v-for="(billingProfile, index) in billingProfiles"
            :id="`profile__${billingProfile.id}`"
            ref="billingProfileSections"
            :key="billingProfile.id"
            :billing-profile="billingProfile"
            class="tw-mt-4"
            @deleted="reloadBillingProfiles"
            :style="{ transitionDelay: `${index * 0.1}s` }"
          />
        </transition-group>
      </template>

      <AddBillingProfileModal
        :show="showAddModal"
        @close="showAddModal = false"
        @created="handleCreated"
      />
    </template>
  </SettingsLayout>
</template>

<style scoped></style>
